import React, { useEffect } from "react";
import { graphql, Link } from "gatsby";
import SEO from "~/views/components/generic/seo";
import structuredData from "~/helpers/structured-data";
import Frame from "~/views/layout/frame";
import Header from "~/views/compositions/navigation/header";
import Flow from "~/models/flow";
import Page from "~/models/page";
import FlowMain from "~/views/compositions/flow/main";
import FlowType from "~/models/flow-type";
import FlowCard from "~/views/components/flow/card";
import Palette from "~/views/layout/palette";
import Breadcrumb from "~/views/compositions/navigation/breadcrumb";
import Service from "~/models/service";
import ServiceCard from "~/views/components/service/card";
import ExploreFooter from "~/views/compositions/explore/footer";

type Props = {
  data: any;
};

const FlowPage = ({ data }: Props) => {
  const page = new Page(data.page as any);
  const flow = new Flow(data.flow as any);

  const services = data.services.nodes.map(
    (service: any) => new Service(service as any)
  );

  page.breadcrumb[2].name = page.breadcrumb[2].name
    .replaceAll("{service.name}", flow.service!.name)
    .replaceAll("{flow.name}", flow.name);
  page.breadcrumb[2].pathname = page.breadcrumb[2].pathname
    .replaceAll("{service.slug}", flow.service!.slug)
    .replaceAll("{flow.slug}", flow.slug);

  useEffect(() => {
    document.getElementsByTagName("body")[0].classList.remove("preventXScroll");
  }, []);

  const flowType = new FlowType(data.flowType as any);

  return (
    <Frame>
      <Header />

      <div className="bg-neutral-99">
        <Breadcrumb
          breadcrumb={page.breadcrumb}
          background="light"
          className="bg-white z-10 relative !pb-5 !px-5 md:!px-14"
        />

        <div className="bg-white">
          <FlowMain flow={flow} />
        </div>
      </div>

      <Palette
        title={flowType.name}
        label={flowType.label}
        slug={flowType.slug}
        className="bg-neutral-99 py-14 md:py-20 !mb-0 sm:mb-0 md:mb-0">
        <ul className="flex gap-2 sm:gap-6 mx-auto overflow-x-auto px-5 md:px-14 hide-scrollbar scroll-px-5 md:scroll-px-14 hide-scrollbar snap-x snap-proximity">
          {flowType.flows.map((flow: any) => (
            <li
              key={flow.id}
              className="shrink-0 w-[180px] sm:min-w-[300px] snap-start">
              <FlowCard flow={flow} />
            </li>
          ))}
        </ul>
      </Palette>

      <section className="mb-12 md:mb-12 bg-neutral-99 pt-0 pb-[120px]">
        <div className="mx-auto mb-8 flex flex-col sm:flex-row gap-3 sm:gap-3 sm:items-center px-5 md:px-14">
          <p className="font-extrabold text-8xl sm:text-10xl text-gray leading-[1.2]">
            {flow.service!.category.label}
          </p>
          <h2 className="ml-[2px] pl-2 sm:pl-3 border-l-2 border-neutral-90 text-2xs font-semibold leading-[1]">
            <Link
              to={`/explore/categories/${flow.service?.category.slug}`}
              className="hover:highlighted-link">
              {flow.service?.category.name}
            </Link>
          </h2>
        </div>
        <ul className="sm:gap-6 mx-auto overflow-x-auto px-0 md:px-14 hide-scrollbar bg-neutral-99">
          {services.map((service: any) => (
            <li key={service.id} className="mb-12 last:mb-0">
              <ServiceCard service={service} />
            </li>
          ))}
        </ul>
      </section>

      <ExploreFooter />
    </Frame>
  );
};

export default FlowPage;

export const Head = ({ data }: Props) => {
  const page = new Page(data.page as any);
  const flow = new Flow(data.flow as any);

  page.title = page.title
    .replaceAll("{service.name}", flow.service!.name)
    .replaceAll("{flow.name}", flow.name);
  page.metaTitle = page.metaTitle
    .replaceAll("{service.name}", flow.service!.name)
    .replaceAll("{flow.name}", flow.name);
  page.url.pathname = `/explore/${flow.service!.slug}/${flow.slug}/`;
  page.description = page.description
    .replaceAll("{service.name}", flow.service!.name)
    .replaceAll("{flow.name}", flow.name);
  page.breadcrumb[2].name = page.breadcrumb[2].name
    .replaceAll("{service.name}", flow.service!.name)
    .replaceAll("{flow.name}", flow.name);
  page.breadcrumb[2].pathname = page.breadcrumb[2].pathname
    .replaceAll("{service.slug}", flow.service!.slug)
    .replaceAll("{flow.slug}", flow.slug);

  const sd = structuredData(page);
  return <SEO page={page} sd={sd} />;
};

export const query = graphql`
  query FlowPage($id: String, $type: String, $category: String) {
    page: pageMdx(
      filter: {
        frontmatter: {
          url: { pathname: { eq: "/explore/{service.slug}/{flow.slug}/" } }
        }
      }
    ) {
      frontmatter {
        id
        title
        description
        breadcrumb {
          id
          yamlId
          name
          pathname
        }
        url {
          yamlId
          name
          pathname
        }
        type
        releasedAt
        updatedAt
        image {
          ...AssetFields
        }
      }
    }
    flow: flowYaml(id: { eq: $id }) {
      id
      yamlId
      title
      slug
      data
      type {
        yamlId
        slug
        name
      }
      posters {
        imgix {
          url(imgixParams: { fm: "webp", auto: "format,compress" })
        }
      }
      videos {
        imgix {
          url
        }
      }
      service {
        id
        yamlId
        title
        slug
        category {
          yamlId
          name
          slug
        }
        logo {
          imgix {
            url(imgixParams: { fm: "webp", auto: "format,compress" })
          }
        }
        flows {
          id
          yamlId
          slug
          title
          type {
            yamlId
            slug
            name
          }
        }
      }
    }
    flowType: flowTypeYaml(slug: { eq: $type }) {
      id
      yamlId
      slug
      name
      flows {
        id
        yamlId
        title
        slug
        type {
          yamlId
          slug
          name
        }
        sys {
          createdAt
        }
        posters {
          imgix {
            url(imgixParams: { fm: "webp", auto: "format,compress" })
          }
        }
        service {
          yamlId
          slug
          title
          category {
            yamlId
            name
            slug
          }
          logo {
            imgix {
              url(imgixParams: { fm: "webp", auto: "format,compress" })
            }
          }
        }
      }
    }
    services: allServiceYaml(
      filter: { category: { slug: { eq: $category } } }
    ) {
      nodes {
        id
        yamlId
        slug
        title
        category {
          yamlId
          name
        }
        logo {
          imgix {
            url(imgixParams: { fm: "webp", auto: "format,compress" })
          }
        }
        flows {
          id
          yamlId
          title
          slug
          type {
            yamlId
            slug
            name
          }
          sys {
            createdAt
          }
          posters {
            imgix {
              url(imgixParams: { fm: "webp", auto: "format,compress" })
            }
          }
          service {
            yamlId
            slug
            title
            category {
              yamlId
              name
              slug
            }
            logo {
              imgix {
                url(imgixParams: { fm: "webp", auto: "format,compress" })
              }
            }
          }
        }
      }
    }
  }
`;
